import clsx from 'clsx'
import { parseISO } from 'date-fns'
import {
  ArrowUpDown,
  Calendar,
  ChevronDown,
  Eye,
  ListFilter,
  Tag
} from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Avatar from '@/components-latest/primitives/Avatar'
import Text from '@/components-latest/primitives/Text'
import DateRangeModal from '@/components-latest/ui/PerformancePage/DateRangeModal'
import { getWalletCashOutStatusLabelKey } from '@/ducks/billing/billing.wallet-funding'
import type { WalletTransactionsDateRangePreset } from '@/ducks/billing/billing.wallet-transactions.filters.helpers'
import { formatWalletTransactionsFilterDate } from '@/ducks/billing/billing.wallet-transactions.filters.helpers'

import { WalletTransaction, WalletTransactionBrand } from '../types'
import { WalletTransactionsPageSize } from '../constants'
import Styles from './TransactionsList.module.scss'
import TransactionsListFooter from './TransactionsListFooter'

type BrandFilterValue = 'kool' | 'yassir' | undefined

type Props = {
  transactions: WalletTransaction[]
  page: number
  limit: WalletTransactionsPageSize
  offset: number
  total?: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: WalletTransactionsPageSize) => void
  onViewDetails?: (transactionId: string, fundingAttemptId?: string) => void
  brandFilter?: BrandFilterValue
  onBrandFilterChange?: (brand: BrandFilterValue) => void
  dateRange?: WalletTransactionsDateRangePreset
  dateFrom?: string
  dateTo?: string
  onDatePresetChange?: (preset: WalletTransactionsDateRangePreset) => void
  onCustomDateRangeChange?: (dateFrom: string, dateTo: string) => void
  onClearDateFilter?: () => void
}

const statusClassName = (status: WalletTransaction['status']) => {
  if (status === 'done') return Styles.statusDone
  if (
    status === 'refund' ||
    status === 'failed' ||
    status === 'canceled'
  ) {
    return Styles.statusRefund
  }
  return Styles.statusPending
}

const statusDotClassName = (status: WalletTransaction['status']) => {
  if (status === 'done') return Styles.statusDotDone
  if (
    status === 'refund' ||
    status === 'failed' ||
    status === 'canceled'
  ) {
    return Styles.statusDotRefund
  }
  return Styles.statusDotPending
}

const brandLabel = (
  brand: WalletTransactionBrand,
  t: (key: string) => string
): string => {
  if (brand === 'kool') return t('billing.wallet.brand-kool')
  if (brand === 'yassir') return t('billing.wallet.brand-yassir')
  return t('billing.wallet.brand-unattributed')
}

const brandClassName = (brand: WalletTransactionBrand): string => {
  if (brand === 'kool') return Styles.brandKool
  if (brand === 'yassir') return Styles.brandYassir
  return Styles.brandUnattributed
}

const TransactionsList = ({
  transactions,
  page,
  limit,
  offset,
  total,
  onPageChange,
  onLimitChange,
  onViewDetails,
  brandFilter,
  onBrandFilterChange,
  dateRange,
  dateFrom,
  dateTo,
  onDatePresetChange,
  onCustomDateRangeChange,
  onClearDateFilter
}: Props) => {
  const { t } = useTranslation()
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)

  const hasCustomDateRange = Boolean(dateFrom && dateTo)

  const handlePresetClick = (preset: WalletTransactionsDateRangePreset) => {
    if (dateRange === preset) {
      onClearDateFilter?.()
      return
    }
    onDatePresetChange?.(preset)
  }

  const customDateRange =
    dateFrom && dateTo
      ? {
          startDate: parseISO(dateFrom),
          endDate: parseISO(dateTo)
        }
      : undefined

  return (
    <div className={Styles.wrapper} data-testid="wallet-transactions-list">
      <Text variant="heading-05" weight="semiBold">
        {t('billing.wallet.transactions-title')}
      </Text>

      <div className={Styles.filtersRow}>
        <div className={Styles.leftFilters}>
          <button type="button" className={Styles.chip} disabled>
            <ListFilter width={16} height={16} />
            <span>{t('billing.wallet.transaction-type')}</span>
            <ChevronDown width={16} height={16} />
          </button>
          <div className={Styles.brandFilterWrapper}>
            <Tag width={16} height={16} />
            <select
              className={Styles.brandSelect}
              value={brandFilter || ''}
              onChange={e => {
                const value = e.target.value as BrandFilterValue
                onBrandFilterChange?.(value || undefined)
              }}
              data-testid="brand-filter-select">
              <option value="">{t('billing.wallet.brand-all')}</option>
              <option value="kool">{t('billing.wallet.brand-kool')}</option>
              <option value="yassir">{t('billing.wallet.brand-yassir')}</option>
            </select>
            <ChevronDown width={16} height={16} className={Styles.brandSelectIcon} />
          </div>
        </div>
        <div className={Styles.periodFilters}>
          <button
            type="button"
            className={clsx(Styles.chip, {
              [Styles.chipActive]: dateRange === 'week'
            })}
            onClick={() => handlePresetClick('week')}
            data-testid="wallet-date-filter-week">
            {t('billing.wallet.last-week')}
          </button>
          <button
            type="button"
            className={clsx(Styles.chip, {
              [Styles.chipActive]: dateRange === 'month'
            })}
            onClick={() => handlePresetClick('month')}
            data-testid="wallet-date-filter-month">
            {t('billing.wallet.last-month')}
          </button>
          <button
            type="button"
            className={clsx(Styles.chip, {
              [Styles.chipActive]: hasCustomDateRange
            })}
            onClick={() => setIsCalendarOpen(true)}
            data-testid="wallet-date-filter-more">
            <Calendar width={16} height={16} />
            <span>{t('billing.wallet.more')}</span>
          </button>
        </div>
      </div>

      <DateRangeModal
        startDate={customDateRange?.startDate ?? null}
        endDate={customDateRange?.endDate ?? null}
        isOpen={isCalendarOpen}
        toggleIsOpen={() => setIsCalendarOpen(current => !current)}
        onApply={(startDate, endDate) => {
          if (!startDate || !endDate) return

          onCustomDateRangeChange?.(
            formatWalletTransactionsFilterDate(startDate),
            formatWalletTransactionsFilterDate(endDate)
          )
        }}
      />

      <div className={Styles.tableCard}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
          <thead>
            <tr>
              <th>
                <span>{t('billing.wallet.table.type')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.date-time')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.brand')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.client')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.order-id')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.status')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.amount')}</span>
                <ArrowUpDown width={16} height={16} />
              </th>
              <th>
                <span>{t('billing.wallet.table.details')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(transaction => (
              <tr key={transaction.id}>
                <td>{transaction.type}</td>
                <td>{transaction.dateTime}</td>
                <td>
                  <span className={`${Styles.brandTag} ${brandClassName(transaction.brand)}`}>
                    {brandLabel(transaction.brand, t)}
                  </span>
                </td>
                <td>
                  {transaction.clientName !== '-' ? (
                    <div className={Styles.clientCell}>
                      <Avatar
                        size="small"
                        string={transaction.clientName}
                        image={transaction.clientAvatar}
                        className={Styles.clientAvatar}
                      />
                      <span>{transaction.clientName}</span>
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td>{transaction.orderId}</td>
                <td>
                  <span
                    className={`${Styles.statusTag} ${statusClassName(
                      transaction.status
                    )}`}>
                    <span
                      className={`${Styles.statusDot} ${statusDotClassName(
                        transaction.status
                      )}`}
                    />
                    {t(
                      getWalletCashOutStatusLabelKey({
                        transactionType: transaction.type,
                        status: transaction.status,
                        cashOutDisplayStatus: transaction.cashOutDisplayStatus
                      })
                    )}
                  </span>
                </td>
                <td
                  className={
                    transaction.amount >= 0
                      ? Styles.amountPositive
                      : Styles.amountNegative
                  }>
                  {`${
                    transaction.amount >= 0 ? '+' : ''
                  }${transaction.amount.toFixed(2)} ${transaction.currency}`}
                </td>
                <td>
                  <button
                    type="button"
                    className={Styles.detailsButton}
                    onClick={() => {
                      onViewDetails?.(transaction.id, transaction.fundingAttemptId)
                    }}>
                    <Eye width={16} height={16} />
                    <span>{t('billing.wallet.view-details')}</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
        <TransactionsListFooter
          page={page}
          limit={limit}
          offset={offset}
          itemCount={transactions.length}
          total={total}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      </div>
    </div>
  )
}

export default TransactionsList
